import React, { useState, useEffect } from 'react';

/**
 * Displays responsible persons for a single entity record.
 * Calls the batch endpoint once with all displayed IDs, then looks up locally.
 * The batch API is called once by the parent (ProductList/SupplierList) and
 * results are passed down via a shared context.
 */
const ResponsiblePersonsCell = React.memo(({ entityType, entityId, lookup }) => {
  if (!lookup) return <span className="text-xs text-slate-400">—</span>;

  const persons = lookup[Number(entityId)];
  if (!persons || persons.length === 0) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  // Group by position for cleaner display
  const groups = {};
  persons.forEach(p => {
    const key = p.position_name || `${t('profile.position')}#${p.position_id}`;
    if (!groups[key]) groups[key] = [];
    const displayName = [(p.first_name || ''), (p.last_name || '')].filter(Boolean).join('');
    const label = displayName ? `${p.employee_id} - ${displayName}` : `#${p.employee_id}`;
    groups[key].push(label);
  });

  return (
    <div className="text-xs space-y-1 max-w-[220px]">
      {Object.entries(groups).map(([posName, labels]) => (
        <div key={posName} className="flex flex-wrap gap-1">
          <span className="font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">
            {posName}:
          </span>
          <span className="text-slate-800 dark:text-slate-200">
            {labels.join('; ')}
          </span>
        </div>
      ))}
    </div>
  );
});

export default ResponsiblePersonsCell;

/**
 * Hook: fetches responsible persons for a batch of entity IDs
 * Returns { lookup: { [entityId]: [...] }, loading: bool }
 */
export function useBatchResponsiblePersons(entityType, entityIds) {
  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ids = entityIds.filter(id => id != null);
    if (ids.length === 0) {
      setLookup({});
      return;
    }

    setLoading(true);
    const params = new URLSearchParams();
    ids.forEach(id => params.append('ids[]', id));

    fetch(`/api/${entityType}s/batch-responsible-persons?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (data.status === 'success') {
          setLookup(data.data || {});
        } else {
          setLookup({});
        }
      })
      .catch(() => setLookup({}))
      .finally(() => setLoading(false));
  }, [entityType, JSON.stringify([...entityIds].sort())]);

  return { lookup, loading };
}
